import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GameOverScreen = ({ selectedCharacter, score, coins, highScore, isPro, onPlayAgain, onWatchAd, onShowLeaderboard }) => {
  return (
    <div
      className="absolute inset-0 z-50 flex flex-col items-center justify-center"
      data-testid="game-over-screen"
      style={{ background: '#fef3c7' }}
    >
      {/* Scattered cartoon stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="absolute text-xl"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${5 + Math.random() * 90}%`,
              animation: `float-particle ${2 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              opacity: 0.3,
            }}
          >&#x2B50;</div>
        ))}
      </div>

      <div className="relative z-10 w-full max-w-xs mx-4 text-center">
        {/* Character bouncing */}
        {selectedCharacter && (
          <div style={{ animation: 'characterFloat 1.5s ease-in-out infinite' }}>
            <div
              className="w-24 h-24 mx-auto mb-3 rounded-full overflow-hidden border-[5px] bg-white"
              style={{
                borderColor: selectedCharacter.color,
                boxShadow: `0 6px 0 ${selectedCharacter.color}88, 0 8px 20px rgba(0,0,0,0.15)`,
              }}
            >
              <img src={selectedCharacter.png} alt="" className="w-full h-full object-contain" />
            </div>
          </div>
        )}

        {/* Title */}
        <h2
          className="text-5xl font-bold mb-1"
          style={{
            fontFamily: 'Fredoka, sans-serif',
            color: '#e94560',
            textShadow: '3px 3px 0 #c62d4a, 0 6px 0 rgba(0,0,0,0.1)',
            transform: 'rotate(-2deg)',
          }}
        >
          Oops!
        </h2>
        <p className="text-orange-800/60 text-sm mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Better luck next time!
        </p>

        {/* Stats bubbles */}
        <div className="flex justify-center gap-3 mb-6">
          {[
            { label: 'Score', value: score, bg: '#fbbf24', shadow: '#d97706', textId: 'final-score' },
            { label: 'Coins', value: coins, bg: '#fb923c', shadow: '#c2410c' },
            { label: 'Best', value: highScore, bg: '#34d399', shadow: '#059669', textId: 'high-score' },
          ].map((s, i) => (
            <div key={i}
              className="w-20 rounded-2xl py-3 text-center"
              style={{
                background: s.bg,
                boxShadow: `0 4px 0 ${s.shadow}, 0 6px 12px rgba(0,0,0,0.1)`,
              }}
            >
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/80" style={{ fontFamily: 'Nunito, sans-serif' }}>{s.label}</p>
              <p className="text-2xl font-bold text-white" data-testid={s.textId} style={{ fontFamily: 'Fredoka, sans-serif' }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <button
            className="w-full py-3 text-white/90 text-sm font-bold rounded-2xl transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              background: '#a855f7',
              boxShadow: '0 4px 0 #7c3aed, 0 6px 12px rgba(0,0,0,0.1)',
            }}
            data-testid="watch-ad-button"
            onClick={onWatchAd}
          >
            Watch Ad to Continue
          </button>

          <button
            className="w-full py-4 text-white text-xl font-bold rounded-2xl transition-all hover:scale-110 active:scale-95"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              background: '#38bdf8',
              boxShadow: '0 5px 0 #0284c7, 0 8px 15px rgba(0,0,0,0.12)',
            }}
            data-testid="play-again-button"
            onClick={onPlayAgain}
          >
            Try Again!
          </button>

          <button
            className="w-full py-3 text-white text-sm font-bold rounded-2xl transition-all hover:scale-105 active:scale-95"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              background: '#f97316',
              boxShadow: '0 4px 0 #c2410c, 0 6px 12px rgba(0,0,0,0.1)',
            }}
            data-testid="leaderboard-button-gameover"
            onClick={onShowLeaderboard}
          >
            Leaderboard
          </button>
        </div>

        {/* Banner Ad for free users */}
        {!isPro && (
          <div className="mt-5 py-2 px-4 rounded-xl bg-white/50 border-2 border-dashed border-orange-300">
            <p className="text-[10px] text-orange-700/50">Advertisement</p>
            <p className="text-xs text-orange-800/60 font-semibold">Banner Ad Space</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Leaderboard modal
export const Leaderboard = ({ isOpen, onClose, currentScore, playerName, characterId }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetch(`${API_URL}/api/leaderboard?limit=20`)
      .then(r => r.json())
      .then(data => { setScores(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setScores([]); setLoading(false); });
  }, [isOpen]);

  if (!isOpen) return null;

  const medal = (i) => i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`;

  return (
    <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/60" data-testid="leaderboard-overlay">
      <div className="bg-slate-800 rounded-3xl p-5 w-80 max-h-[520px] shadow-2xl border border-white/10 flex flex-col">
        <h2 className="text-2xl font-bold text-amber-400 mb-4 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Leaderboard
        </h2>
        
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-3 border-white/20 border-t-amber-400 rounded-full" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : scores.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-white/40 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>No scores yet. Be the first!</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1" style={{ maxHeight: '360px' }}>
            {scores.map((entry, i) => (
              <div key={i}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                  entry.name === playerName && entry.score === currentScore ? 'bg-amber-500/20 border border-amber-400/30' : 'bg-white/5'
                }`}
              >
                <span className="w-8 text-center text-sm font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: i < 3 ? '#fbbf24' : '#ffffff80' }}>
                  {medal(i)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>{entry.name}</p>
                  <p className="text-white/40 text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>{entry.character} &middot; Lvl {(entry.level || 0) + 1}</p>
                </div>
                <span className="text-amber-400 text-lg font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>{entry.score}</span>
              </div>
            ))}
          </div>
        )}
        
        <button
          onClick={onClose}
          className="w-full mt-4 py-2.5 rounded-xl bg-white/10 text-white text-sm font-semibold hover:bg-white/20 transition-all"
          style={{ fontFamily: 'Fredoka, sans-serif' }}
          data-testid="leaderboard-close"
        >
          Close
        </button>
      </div>
    </div>
  );
};
